import { Template, CreateTemplateParams } from './types';
import { IVFSResponse } from '../types';
import BaseAPI from '../BaseAPI';

/**
 * Template API
 * Handles all template-related operations
 */
class TemplatesAPI extends BaseAPI {
  /**
   * Create a new template
   */
  async createTemplate(
    params: CreateTemplateParams
  ): Promise<IVFSResponse<Template>> {
    return this.wrapResponse<Template>({
      promise: this.apiInstance.post('/v2/templates', params),
      errorMessage: 'Failed to create template',
    });
  }

  /**
   * Get all templates
   */
  async getTemplates(): Promise<IVFSResponse<Template[]>> {
    return this.wrapResponse<Template[]>({
      promise: this.apiInstance.get('/v2/templates'),
      errorMessage: 'Failed to get templates',
    });
  }

  /**
   * Get a template by ID
   */
  async getTemplate(id: string): Promise<IVFSResponse<Template>> {
    return this.wrapResponse<Template>({
      promise: this.apiInstance.get(`/v2/templates/${id}`),
      errorMessage: 'Failed to get template',
    });
  }

  /**
   * Update a template
   */
  async updateTemplate(
    id: string,
    template: Partial<
      Omit<
        Template,
        '_id' | 'createdAt' | 'updatedAt' | 'id' | 'ownerId' | 'teamId'
      >
    >
  ): Promise<IVFSResponse<Template>> {
    return this.wrapResponse<Template>({
      promise: this.apiInstance.put<IVFSResponse<Template>>(
        `/v2/templates/${id}`,
        template
      ),
      errorMessage: 'Failed to update template',
    });
  }

  /**
   * Delete a template
   */
  async deleteTemplate(id: string): Promise<IVFSResponse<void>> {
    return this.wrapResponse<void>({
      promise: this.apiInstance.delete(`/v2/templates/${id}`),
      errorMessage: 'Failed to delete template',
    });
  }

  /**
   * Generate a title for a custom template
   */
  async generateTitle(customTemplate: string): Promise<IVFSResponse<string>> {
    return this.wrapResponse<string>({
      promise: this.apiInstance.post('/v2/proxy/ai/completion', {
        prompt: `Generate a concise title (max 15 characters, use complete words, no abbreviations) for this template prompt: "${customTemplate.trim()}"`,
        model: 'fast',
      }),
      errorMessage: 'Failed to generate title',
    });
  }
}

export default TemplatesAPI;
